import { inject, Injectable } from '@angular/core'

import { sortBy } from 'lodash-es'
import { combineLatest, firstValueFrom, map, shareReplay, takeUntil } from 'rxjs'

import { Memoize } from '@ti/core/common/decorators'
import { injectDestroy$ } from '@ti/core/common/utils'
import { LanguageService } from '@ti/core/ui-kit/i18n'

import { DriversStore } from './drivers.store'

@Injectable({ providedIn: 'root' })
export class DriversOptionsProvider {
  protected readonly driversStore = inject(DriversStore)
  protected readonly languageService = inject(LanguageService)
  protected readonly destroy$ = injectDestroy$()

  @Memoize()
  public get filterOptions$() {
    return combineLatest([
      this.languageService.translate$('reports.filter.no-driver'),
      this.driversStore.state$.items,
    ]).pipe(
      takeUntil(this.destroy$),
      map(([noDriver, drivers]) => {
        const options =
          drivers.map((driver) => ({
            label: driver.name,
            value: driver.id,
          })) ?? []

        const sortedOptions = sortBy(options, 'label')
        return [{ label: `${noDriver} (---)`, value: null }, ...sortedOptions]
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    )
  }

  @Memoize()
  public get selectOptions$() {
    return this.driversStore.state$.items.pipe(
      takeUntil(this.destroy$),
      map((drivers) => {
        return Array.isArray(drivers)
          ? sortBy(
              drivers.map((driver) => ({ label: driver.name, value: driver.id })),
              'label',
            )
          : []
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    )
  }

  // The store starts empty until its first load, so reading it earlier would offer no drivers
  public async getFilterOptions() {
    await this.driversStore.ensureIsFresh()
    return firstValueFrom(this.filterOptions$)
  }

  public async getDriverOptionsList() {
    const options = await this.getFilterOptions()

    return options.filter((item) => item.value !== null)
  }

  public async getSelectOptions() {
    await this.driversStore.ensureIsFresh()
    return firstValueFrom(this.selectOptions$)
  }
}
